"use client";

import React from "react";

import { useTranslations } from "next-intl";

import { usePropertyOptions } from "@/hooks/usePropertyOptions";
import { cn } from "@/utils/cn";

export interface PropertyStatusDisplayProps {
  /** Property status */
  status: string;
  /** Custom status text override */
  customStatusText?: string;
  /** CSS class name for custom styling */
  className?: string;
  /** Show label "Статус:" */
  showLabel?: boolean;
  /** Compact mode */
  compact?: boolean;
}

export const PropertyStatusDisplay: React.FC<PropertyStatusDisplayProps> = ({
  status,
  customStatusText,
  className,
  showLabel = true,
  compact = false,
}) => {
  const tRealtor = useTranslations("realtor");
  const { getPublicationStatuses } = usePropertyOptions();
  const t = useTranslations();

  if (compact) {
    // Компактный режим - только бейдж без лейбла
    return (
      <div
        className={cn(
          "inline-flex items-center justify-center",
          "h-[50px] px-4", // Точные размеры из Figma
          "bg-white border border-[#ECECEC]", // Точные цвета из Figma
          "rounded-[15px]", // Точный border-radius из Figma
          "shadow-[5px_5px_25px_15px_rgba(235,235,235,0.25)]", // Точная тень из Figma
          className,
        )}
        data-testid={`property-status-${status}`}
        role="status"
        aria-label={tRealtor("statusAriaLabel", {
          status: t(`status.${status}`),
        })}
      >
        <span
          className={cn(
            "text-[16px] font-medium text-[#444444] leading-[20px]",
          )}
        >
          c
        </span>
      </div>
    );
  }

  // Полный режим с лейблом (как в новой Figma 6001:3)
  return (
    <div
      className={cn(
        "flex items-center gap-4",
        "h-[50px]", // Обновленные размеры из новой Figma
        className,
      )}
      data-testid="property-status-display"
    >
      {/* Лейбл "Статус:" - левая часть */}
      {showLabel && (
        <span
          className={cn(
            "text-[15px] font-medium text-[#373737] leading-[19px]", // Точная типографика из Figma
            "mr-[15px]", // Отступ до бейджа
          )}
        >
          {tRealtor("statusLabel")}
        </span>
      )}

      {/* Бейдж статуса - правая часть */}
      <div
        className={cn(
          "inline-flex items-center justify-center",
          " h-[50px] px-4", // Точные размеры из Figma
          "bg-white border border-[#ECECEC]", // Точные цвета из Figma
          "rounded-[15px]", // Точный border-radius из Figma
          "shadow-[5px_5px_25px_15px_rgba(235,235,235,0.25)]", // Точная тень из Figma
        )}
        role="status"
        aria-label={tRealtor("statusAriaLabel", {
          status: t(`status.${status}`),
        })}
      >
        <span
          className={cn(
            "text-[16px] font-medium text-[#444444] leading-[20px]", // Точная типографика из Figma
            "text-center",
          )}
        >
          {t(`status.${status}`)}
        </span>
      </div>
    </div>
  );
};

export default PropertyStatusDisplay;
